import type { NetWorthDto, UnpricedHoldingDto } from "@gladis/shared";
import { formatEur } from "@gladis/shared";
import { StatCard } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import type { ReactNode } from "react";
import { formatMonth } from "@gladis/ui/i18n";

/**
 * ① The Net Worth Card — ONE FIGURE, and its trend.
 *
 * ==========================================================================================
 * THE COMPOSITION MOVED TO THE CHART ON 2026-08-14
 * ==========================================================================================
 *
 * This card carried the headline PLUS four pillar rows with their shares PLUS the loan-to-value ratio —
 * six figures in the first card of a row whose other three hold one each. A metric card that holds a
 * decomposition is a different kind of object from the three beside it, and the row stops being
 * scannable, which is the thing `StatCard`'s own documentation warns about.
 *
 * The rows went to the net worth chart's card, which draws those exact four layers: AC-D20's shares and
 * AC-D21's LTV are unchanged, and they now sit with the picture of themselves rather than above it.
 *
 * WHAT STAYED IS ONE METRIC AND ITS QUALIFIERS. The trend is not a second metric — it is the same figure
 * measured against a month a year ago — and the hint says what the total excludes. Both belong to the
 * number they are about.
 *
 * ① The Net Worth Summary Card — the sentence the page exists to say.
 *
 * ==========================================================================================
 * WHY THIS LIVES IN `apps/app` WHEN EVERY OTHER FEATURE COMPONENT DOES NOT
 * ==========================================================================================
 *
 * `README.md` says feature UI lives in the domain packages, not here, and every module page obeys
 * it. The dashboard cannot: it belongs to no bounded context, `apps/app` may not import
 * `@gladis/reporting` (a Node package that must never hold React), and `@gladis/ui` is for
 * primitives and may import no domain. The exception is real and is recorded in the architecture
 * documents rather than left for the next reader to infer.
 *
 * ==========================================================================================
 * EVERY FIGURE IS A STRING FROM THE SERVER, PARSED ONCE, HERE
 * ==========================================================================================
 *
 * Cents cross the wire as decimal strings because `JSON.stringify` cannot carry a bigint and a
 * `number` reintroduces float in a page that is sums of sums. They become numbers at the point of
 * FORMATTING and nowhere earlier, so nothing downstream can accumulate them again.
 */

/** Cents-as-string → the euro amount `formatEur` wants. The only place this conversion happens. */
function euros(cents: string): number {
  return Number(cents) / 100;
}

/**
 * What the headline excludes, as a sentence — extracted so it can be tested without a DOM.
 *
 * AC-D9 requires BOTH halves: the holding is left out of the figure AND the card says so. Excluded and
 * silent understates net worth by the whole position while looking complete, which is the outcome the
 * criterion names — and `apps/app` has no render harness, so the half that lives in markup would otherwise
 * be the only part of this feature asserted by nothing.
 *
 * The SYMBOLS rather than the names: a symbol is what the member typed and what the Investments page lists,
 * and a card is not the place to spell out "Koninklijke Ahold Delhaize N.V.".
 */
export function unpricedSentence(holdings: readonly UnpricedHoldingDto[]): string {
  const subject = holdings.length === 1 ? "a holding" : `${holdings.length} holdings`;
  const symbols = holdings.map((holding) => holding.symbol).join(", ");

  return `Excludes ${subject} with no recorded price: ${symbols}.`;
}

export function NetWorthCard({ netWorth }: { netWorth: NetWorthDto }): ReactNode {
  const t = useT();
  return (
    <StatCard
      label={t("dash.netWorth")}
      value={formatEur(euros(netWorth.totalCents))}
      /*
       * A GLYPH AND A SIGN, never a colour (AC-D23). `StatCard` renders the arrow itself and its own
       * documentation gives the reason: red/green is the commonest colour confusion there is, and
       * whether up is good is not this card's judgement to make — savings rising and expenses rising
       * are the same arrow and opposite news.
       *
       * Absent entirely for a household with no month a year ago, with no substitute comparison
       * (AC-D19a). `trend` being undefined is the whole of that rule; the component already omits
       * the glyph when it is not given one.
       */
      {...(netWorth.trend === undefined ? {} : { trend: netWorth.trend.direction })}
      hint={<NetWorthHint netWorth={netWorth} />}
      testId="net-worth-card"
      valueTestId="net-worth-total"
    />
  );
}

/**
 * What qualifies the headline: the year-ago comparison, and anything the total excludes.
 *
 * The unpriced disclosure is not decoration. AC-D9 requires BOTH halves — the holding is excluded
 * from the total AND the card says so — because excluded-and-silent understates net worth by the
 * whole position while looking complete.
 */
function NetWorthHint({ netWorth }: { netWorth: NetWorthDto }): ReactNode {
  const t = useT();
  const { trend, unpricedHoldings } = netWorth;

  /*
   * "RIGHT NOW", BECAUSE THE FILTERS LOOK PAGE-WIDE AND THIS FIGURE IGNORES THEM.
   *
   * They moved into the title row on 2026-08-14 and govern the three flow cards, the ring and the expense
   * ranking. Net worth is not one of them and CANNOT BE: a balance has no per-period form, and
   * `dashboard.md`'s non-goals record that a per-member breakdown "would need ownership on investments and
   * properties that nothing records". Two words is what that costs to say, and the alternative is a
   * reader picking a member and wondering why one figure in the row did not move.
   */
  const window = <span>{t("dash.netWorthScope")}</span>;

  const excluded =
    unpricedHoldings.length === 0 ? null : (
      <span data-testid="net-worth-unpriced" className="mt-1 block">
        {unpricedSentence(unpricedHoldings)}
      </span>
    );

  if (trend === undefined)
    return (
      <>
        {window}
        {excluded}
      </>
    );

  const delta = Number(trend.deltaCents) / 100;
  const [year = "", month = ""] = trend.comparedToMonth.split("-");
  const label = formatMonth(Number(year), Number(month), "short");

  return (
    <>
      {window}
      {/* Signed always, including zero. Dropping the sign makes a rise and a fall look the same. */}
      <span data-testid="net-worth-trend" className="mt-1 block">
        {delta > 0 ? "+" : delta < 0 ? "−" : ""}
        {formatEur(Math.abs(delta))} vs {label}
      </span>
      {excluded}
    </>
  );
}
